import { useCallback, useMemo, useState } from 'react';
import { ChevronDown, ChevronUp, ExternalLink, Upload } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/hooks/useAuth';
import type { Session, VideoFile } from '@/types';
import VideoFilesList, { type VideoUploadState } from './VideoFilesList';
import SessionRecordingsGrid from './SessionRecordingsGrid';
import { useSessionRecordings } from './useSessionRecordings';

interface SessionVideosSectionProps {
  session: Session;
}

// Issue #1000: this section no longer embeds a player. Recordings render as
// thumbnail cards that deep-link into the analytics tool; raw file
// management (upload, per-file actions) stays in the collapsible list below.
export default function SessionVideosSection({
  session,
}: SessionVideosSectionProps) {
  const { t } = useTranslation('pages');
  const { user } = useAuth();
  const isSessionOwner = user?.id === session.userId;

  const [videos, setVideos] = useState<VideoFile[]>([]);
  const [areFilesExpanded, setAreFilesExpanded] = useState(false);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [uploadState, setUploadState] = useState<VideoUploadState>({
    status: 'idle',
    progress: 0,
    filename: null,
  });

  const isUploading = uploadState.status === 'uploading';
  const uploadButtonLabel = isUploading
    ? t('sessions.detail.videoFiles.uploading', {
        progress: uploadState.progress,
      })
    : t('sessions.detail.videoFiles.upload');

  const availableVideos = useMemo(
    () => videos.filter((video) => video.uploadStatus === 'completed'),
    [videos],
  );
  const availableVideoIds = useMemo(
    () =>
      availableVideos
        .map((video) => video.id)
        .sort()
        .join(','),
    [availableVideos],
  );

  const { recordings, areRecordingsLoading } = useSessionRecordings(
    session.id,
    availableVideos,
    availableVideoIds,
  );

  const handleVideosChange = useCallback((nextVideos: VideoFile[]) => {
    setVideos(nextVideos);
  }, []);

  const handleUploadStateChange = useCallback(
    (nextUploadState: VideoUploadState) => {
      setUploadState(nextUploadState);
      if (nextUploadState.status === 'uploading') {
        setAreFilesExpanded(true);
      }
    },
    [],
  );

  const filesPanelId = `session-${session.id}-video-files`;

  return (
    <section className="mb-8 rounded-lg border border-secondary-200 bg-white">
      <div className="border-b border-secondary-100 p-4 sm:p-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <h3 className="text-lg font-semibold text-secondary-900">
            {t('sessions.detail.videoFiles.title')}
          </h3>
          <div className="flex items-center gap-4 self-start sm:self-center">
            {session.visibility === 'public' && (
              <a
                href={`/watch/${session.id}`}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1.5 text-sm font-medium text-primary-500 hover:text-primary-600 hover:underline"
              >
                <ExternalLink className="h-4 w-4" aria-hidden="true" />
                <span>{t('sessions.detail.videoFiles.openWatchPage')}</span>
              </a>
            )}
            {isSessionOwner && (
              <button
                type="button"
                onClick={() => setIsUploadModalOpen(true)}
                title={uploadButtonLabel}
                className="btn-secondary flex items-center space-x-2 whitespace-nowrap px-4 py-2"
              >
                <Upload className="h-4 w-4" aria-hidden="true" />
                <span>{uploadButtonLabel}</span>
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="space-y-4 p-4 sm:p-6">
        <SessionRecordingsGrid
          sessionId={session.id}
          recordings={recordings}
          areRecordingsLoading={areRecordingsLoading}
          hasAvailableVideos={availableVideos.length > 0}
          isSessionOwner={isSessionOwner}
        />

        <div className="overflow-hidden rounded-lg border border-secondary-200">
          <button
            type="button"
            aria-controls={filesPanelId}
            aria-expanded={areFilesExpanded}
            onClick={() => setAreFilesExpanded((isExpanded) => !isExpanded)}
            className="flex w-full items-center justify-between gap-3 bg-secondary-50 px-4 py-3 text-left transition-colors hover:bg-secondary-100"
          >
            <span className="flex items-center gap-2">
              <span className="font-medium text-secondary-900">
                {t('sessions.detail.videoFiles.filesTitle')}
              </span>
              <span className="rounded-full bg-secondary-200 px-2 py-0.5 text-xs text-secondary-600">
                {videos.length}
              </span>
            </span>
            <span className="flex items-center gap-2 text-sm text-secondary-500">
              {areFilesExpanded
                ? t('sessions.detail.videoFiles.hideFiles')
                : t('sessions.detail.videoFiles.showFiles')}
              {areFilesExpanded ? (
                <ChevronUp className="h-4 w-4" aria-hidden="true" />
              ) : (
                <ChevronDown className="h-4 w-4" aria-hidden="true" />
              )}
            </span>
          </button>

          <VideoFilesList
            session={session}
            recordings={recordings}
            onVideosChange={handleVideosChange}
            onUploadStateChange={handleUploadStateChange}
            showUploadButton={false}
            isUploadModalOpen={isUploadModalOpen}
            onUploadModalOpenChange={setIsUploadModalOpen}
            isContentVisible={areFilesExpanded}
            contentId={filesPanelId}
            contentClassName="border-t border-secondary-200 p-4 sm:p-6"
          />
        </div>
      </div>
    </section>
  );
}
